import React, { useState, useEffect } from 'react';
import Footer from "../views/Footer";
import FadeIn from "../components/FadeIn";
import { Container, Typography, ImageCard } from "../components/ui";
import SkeletonLoader from "../components/SkeletonLoader";
import googleDriveService from '../services/googleDriveService';

const EXTRA_GALLERY_ITEMS = [
  {
    id: 'youtube-short-1',
    category: 'youtube',
    title: 'BIM Bytes Academy Short 1',
    description: 'Watch this YouTube short to see our training highlights.',
    imageUrl: 'https://img.youtube.com/vi/EDzSgF9WTTA/hqdefault.jpg',
    externalUrl: 'https://youtube.com/shorts/EDzSgF9WTTA?si=lKuKZufTRh8ko9EU',
    date: new Date().toISOString().split('T')[0],
  },
  {
    id: 'youtube-short-2',
    category: 'youtube',
    title: 'BIM Bytes Academy Short 2',
    description: 'Watch this YouTube short for a quick glimpse into our academy.',
    imageUrl: 'https://img.youtube.com/vi/CY9bbjrsIW8/hqdefault.jpg',
    externalUrl: 'https://youtube.com/shorts/CY9bbjrsIW8?si=2Akd9mP3cBKgdrRB',
    date: new Date().toISOString().split('T')[0],
  }
];

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  // Load gallery images from Google Drive
  useEffect(() => {
    let isMounted = true;

    const loadGalleryImages = async () => {
      try {
        setLoading(true);
        console.log('🔄 Initializing Google Drive Service...');
        // Initialize the service
        const initialized = await googleDriveService.initialize();

        if (!initialized) {
          console.error('❌ Google Drive Service not initialized. Check API key and folder ID in src/data/galleryData.js');
          setLoading(false);
          return;
        }
        // console.log('✅ Google Drive Service initialized');

        // // Auto-detect folders and images from Google Drive
        // console.log('🔍 Auto-detecting gallery structure from Google Drive...');
        const result = await googleDriveService.autoDetectGalleryStructure();

        if (isMounted) {
          console.log('📊 Gallery data loaded:', {
            foldersCount: result.folders.length,
            imagesCount: result.images.length,
            extraItems: EXTRA_GALLERY_ITEMS.length
          });

          setGalleryItems([...result.images, ...EXTRA_GALLERY_ITEMS]);

          const youtubeCategory = {
            id: 'youtube',
            category: 'youtube',
            name: 'YouTube',
            icon: '▶️',
            description: 'YouTube Shorts'
          };

          const combinedCategories = result.folders.some((category) => category.category === 'youtube')
            ? result.folders
            : [...result.folders, youtubeCategory];

          setCategories(combinedCategories);
        }

        setLoading(false);

      } catch (error) {
        console.error('❌ Error loading gallery from Google Drive:', error);
        if (isMounted) {
          setGalleryItems([]);
          setCategories([]);
          setLoading(false);
        }
      }
    };

    loadGalleryImages();

    // Cleanup on component unmount
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter items based on category only
  const filteredItems = galleryItems.filter(item => {
    const matches = activeCategory === 'all' || item.category === activeCategory;
    console.log(`🔍 Filtering: item.category="${item.category}" vs activeCategory="${activeCategory}" -> ${matches}`);
    return matches;
  });

  return (
    <>
      {/* Hero Section */}
      <Container.Section padding="hero" background="gradient">
        <Container size="standard">
          <FadeIn delay={0.2} direction="down" fullWidth>
            <Typography.SectionHeader
              title={
                <>
                  LIFE AT BIM BYTES—<span className="text-gradient">EVENTS THAT BRING US TOGETHER</span>
                </>
              }
              description="Explore our student projects, training sessions, facilities, and events. See the quality of work and learning environment at BimBytes Academy."
              align="center"
            />
          </FadeIn>
        </Container>
      </Container.Section>

      {/* Category Filter Section */}
      <Container.Section padding="compact" background="white" className="pb-4 lg:pb-6">
        <Container size="standard">
          <FadeIn delay={0.3} direction="up" fullWidth>
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-4 mb-4">
              {/* All Categories Button */}
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ease-smooth ${activeCategory === 'all'
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-gray-100 text-textSecondary hover:bg-primary/10 hover:text-primary'
                  }`}
              >
                All
              </button>

              {/* Dynamic Categories from Apps Script */}
              {loading ? (
                // Category skeleton buttons
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="px-6 py-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded-xl w-24 h-12"></div>
                ))
              ) : categories.length > 0 ? (
                categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.category)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ease-smooth ${activeCategory === category.category
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-gray-100 text-textSecondary hover:bg-primary/10 hover:text-primary'
                      }`}
                  >
                    {category.name}
                  </button>
                ))
              ) : null}
            </div>
          </FadeIn>
        </Container>
      </Container.Section>

      {/* Gallery Grid */}
      <Container.Section padding="content" background="white" className="pt-4 lg:pt-6">
        <Container size="standard">
          {loading ? (
            <div className="text-center py-16">
              <SkeletonLoader type="gallery" count={6} />
              <Typography.Body color="secondary" className="mt-8">
                Loading gallery from Google Drive...
              </Typography.Body>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <Typography.H3 color="secondary" className="mb-4">
                No images found
              </Typography.H3>
              <Typography.Body color="muted">
                Check that your Google Drive folder ID 
              </Typography.Body>
            </div>
          ) : (
            <Container.Grid columns="3" gap="default">
              {filteredItems.map((item, index) => (
                <FadeIn key={item.id || index} delay={0.1 + index * 0.05} direction="up">
                  <ImageCard
                    item={item}
                  />
                </FadeIn>
              ))}
            </Container.Grid>
          )}
        </Container>
      </Container.Section>

      <Footer />
    </>
  );
};

export default Gallery;




